import { useEffect, useRef } from 'react';
import { useNylasAuthStore } from '../store/nylasAuth';
import { syncCalendarFromNylas } from '../lib/nylasCalendarService';
import { isNylasDisabled } from '../lib/nylasCache';

const BASE_INTERVAL = 30 * 60 * 1000; // 30 minutes
const MAX_INTERVAL = 90 * 60 * 1000; // 90 minutes
const STARTUP_DELAY_BASE = 15000; // 15 seconds base + jitter
const MAX_CONSECUTIVE_FAILURES = 3;
const MIN_VISIBILITY_SYNC_GAP = 20 * 60 * 1000; // 20 minutes minimum between visibility-triggered syncs

let isSyncInProgress = false;

export function useAutomaticCalendarSync() {
  const { isConnected } = useNylasAuthStore();
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncRef = useRef<number>(0);
  const consecutiveFailuresRef = useRef<number>(0);

  useEffect(() => {
    if (!isConnected()) {
      return;
    }

    const getInterval = () => {
      const failures = consecutiveFailuresRef.current;
      if (failures === 0) return BASE_INTERVAL;
      return Math.min(BASE_INTERVAL * Math.pow(2, failures), MAX_INTERVAL);
    };

    const performSync = async () => {
      if (isSyncInProgress) return;
      if (isNylasDisabled()) return;
      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) return;

      try {
        isSyncInProgress = true;
        await syncCalendarFromNylas();
        lastSyncRef.current = Date.now();
        consecutiveFailuresRef.current = 0;
      } catch (error: any) {
        consecutiveFailuresRef.current += 1;
        if (consecutiveFailuresRef.current < MAX_CONSECUTIVE_FAILURES) {
          console.warn(`Calendar sync failed (${consecutiveFailuresRef.current}/${MAX_CONSECUTIVE_FAILURES}), backing off`);
        }
      } finally {
        isSyncInProgress = false;
      }
    };

    const startupDelay = STARTUP_DELAY_BASE + Math.random() * 5000;
    const startupTimer = setTimeout(() => {
      const timeSinceLastSync = Date.now() - lastSyncRef.current;
      if (timeSinceLastSync > BASE_INTERVAL) {
        performSync();
      }
    }, startupDelay);

    syncIntervalRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        performSync();
      }
    }, getInterval());

    return () => {
      clearTimeout(startupTimer);
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [isConnected()]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        if (isNylasDisabled()) return;
        const timeSinceLastSync = Date.now() - lastSyncRef.current;
        if (timeSinceLastSync > MIN_VISIBILITY_SYNC_GAP && isConnected() && !isSyncInProgress) {
          if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
            consecutiveFailuresRef.current = 0;
          }
          try {
            isSyncInProgress = true;
            await syncCalendarFromNylas();
            lastSyncRef.current = Date.now();
            consecutiveFailuresRef.current = 0;
          } catch (error) {
            consecutiveFailuresRef.current += 1;
          } finally {
            isSyncInProgress = false;
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isConnected]);
}
